import { useCallback, useState } from 'react'
import { useWorkspaceStore } from '../../stores/workspaceStore'
import { workspacesApi } from '../../services/api'
import { Badge, Card, CardHeader, Toggle } from '../../ds'

/**
 * A store fact about how the merchant treats products that are no longer for sale.
 *
 * The persisted key deliberately remains under `agentSettings`: moving the merchant-facing
 * ownership must not change the runtime contract consumed by product search. Absent remains
 * true for backwards compatibility with workspaces created before the key existed.
 */
const ProductPolicyCard = () => {
  const { currentWorkspace, setCurrentWorkspace } = useWorkspaceStore()
  const [isUpdating, setIsUpdating] = useState(false)
  const [saved, setSaved] = useState(false)

  const mentionDiscontinued = currentWorkspace?.agentSettings?.mentionDiscontinuedProducts ?? true

  const handleToggle = useCallback(async () => {
    if (!currentWorkspace || isUpdating) return

    const workspaceId = currentWorkspace.id
    setIsUpdating(true)
    setSaved(false)
    const newValue = !mentionDiscontinued

    try {
      const response = await workspacesApi.update(workspaceId, {
        agentSettings: {
          ...currentWorkspace.agentSettings,
          mentionDiscontinuedProducts: newValue,
        },
      } as never)

      if (response.success) {
        // A workspace switch can happen while this request is in flight. Never put the
        // workspace that originated the request back on screen after the merchant has moved on.
        const latestWorkspace = useWorkspaceStore.getState().currentWorkspace
        if (!latestWorkspace || latestWorkspace.id !== workspaceId) return

        setCurrentWorkspace({
          ...latestWorkspace,
          agentSettings: { ...latestWorkspace.agentSettings, mentionDiscontinuedProducts: newValue },
        })
        setSaved(true)
        setTimeout(() => setSaved(false), 2000)
      }
    } catch (error) {
      console.error('Failed to update product policy:', error)
    } finally {
      setIsUpdating(false)
    }
  }, [currentWorkspace, isUpdating, mentionDiscontinued, setCurrentWorkspace])

  return (
    <Card>
      <CardHeader
        title="Product policy"
        description="Tell Marva whether products you no longer sell remain useful context for customers."
        aside={saved ? <Badge tone="sage">Saved</Badge> : undefined}
      />
      <Toggle
        checked={mentionDiscontinued}
        onChange={() => void handleToggle()}
        busy={isUpdating}
        label="Discontinued products remain part of our product history"
        description="When this is on, Marva may refer to an archived or discontinued product when it is relevant, and will say that it is no longer available. Draft products are always private. When it is off, Marva leaves archived and discontinued products out of product search and recommendations."
      />
    </Card>
  )
}

export default ProductPolicyCard
